import type { Column } from 'react-table';
import { ContextMenuButton, TableCell, TableHeader } from 'redesign';
import { pluralizeWithValue } from 'shared/utils/pluralize';
import { ArchiveTableColumnDefinition } from 'ogp/components/views/administration/shifts-checkouts/archive/components/table/helpers/archive-table.types';
import { Box, styled, Tooltip, Typography } from '@mui/material';
import { IconCertificate, IconWarning } from 'assets/img';
import { convertToExperienceFormat } from 'shared/components/flex-workers-for-work-table/utils/helpers';
import { EmploymentType } from '@types';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { ContractHoursChip } from 'shared/components/flex-workers-for-work-table/utils/contract-hours-chip';
import { ApplicantCellProps, ApplicantColumnDefinition } from './applicant-table.types';
import { useGetShiftDetailsColumnDefinitionBase } from '../../../shared/columns/use-get-shift-details-column-definition-base';
import { HourlyRateCell } from '../hourly-rate-cell';

export const useGetApplicantColumnDefinition: <TAction extends object>(
  employmentType: EmploymentType,
  isProjectTable: boolean,
  options?: { showAppliedShifts?: boolean; hideEmploymentTypeFields?: boolean }
) => Column<ApplicantColumnDefinition<TAction>>[] = (
  employmentType,
  projectTable,
  { showAppliedShifts = true, hideEmploymentTypeFields = false } = { showAppliedShifts: true }
) => {
  const [flexWorkerImage] = useGetShiftDetailsColumnDefinitionBase();
  const { t } = useTranslation('hq-screen-specific');

  return useMemo(() => {
    const flexWorkerFullName: Column<ApplicantColumnDefinition> = {
      Header: () => (
        <TableHeader>
          {t('work.applicants.table_headers.flexworker', { ns: 'hq-screen-specific' })}
        </TableHeader>
      ),
      accessor: 'flexWorkerFullName',
      Cell: FlexWorkerCell,
    };

    const hasRequiredCertificates: Column<ApplicantColumnDefinition> = {
      Header: () => (
        <TableHeader>
          {t('work.applicants.table_headers.has_certificate', { ns: 'hq-screen-specific' })}
        </TableHeader>
      ),
      accessor: 'hasRequiredCertificates',
      Cell: CertificateChecksCell,
    };

    const rating: Column<ApplicantColumnDefinition> = {
      Header: () => (
        <TableHeader>
          {t('work.applicants.table_headers.rating', { ns: 'hq-screen-specific' })}
        </TableHeader>
      ),
      accessor: 'rating',
      Cell: RatingCell,
    };

    const attendanceRate: Column<ApplicantColumnDefinition> = {
      Header: () => (
        <TableHeader>
          {t('work.applicants.table_headers.attendance_rate', { ns: 'hq-screen-specific' })}
        </TableHeader>
      ),
      accessor: 'attendanceRate',
      Cell: AttendanceRateCell,
    };

    const workedShifts: Column<ApplicantColumnDefinition> = {
      Header: () => (
        <TableHeader>
          {t('work.applicants.table_headers.worked_shifts', { ns: 'hq-screen-specific' })}
        </TableHeader>
      ),
      accessor: 'workedShifts',
      Cell: WorkedShiftsCell,
    };

    const hourlyRate: Column<ApplicantColumnDefinition> = {
      Header: () => (
        <TableHeader>
          {t('work.applicants.table_headers.hourly_rate', { ns: 'hq-screen-specific' })}
        </TableHeader>
      ),
      accessor: 'hourlyRate',
      Cell: HourlyRateCell,
    };

    const appliedShifts: Column<ApplicantColumnDefinition> = {
      Header: () => (
        <TableHeader>
          {t('work.applicants.table_headers.applied_shifts', { ns: 'hq-screen-specific' })}
        </TableHeader>
      ),
      accessor: 'appliedShifts',
      Cell: AppliedShiftsCell,
    };

    const phone: Column<ApplicantColumnDefinition> = {
      Header: () => (
        <TableHeader>
          {t('work.applicants.table_headers.phone', { ns: 'hq-screen-specific' })}
        </TableHeader>
      ),
      accessor: 'phone',
      Cell: PhoneCell,
    };

    const experience: Column<ApplicantColumnDefinition> = {
      Header: () => (
        <TableHeader>
          {t('work.applicants.table_headers.work_experiences', { ns: 'hq-screen-specific' })}
        </TableHeader>
      ),
      accessor: 'experienceInMonths',
      Cell: ExperienceCell,
    };

    const contractHours: Column<ApplicantColumnDefinition> = {
      Header: () => (
        <TableHeader>{t('worker_data.contract_hours', { ns: 'all-platforms-common' })}</TableHeader>
      ),
      accessor: 'contractHoursData',
      Cell: ContractHoursCell,
    };

    const distance: Column<ApplicantColumnDefinition> = {
      Header: () => (
        <TableHeader>
          {t('work.applicants.table_headers.distance', { ns: 'hq-screen-specific' })}
        </TableHeader>
      ),
      accessor: 'distanceInKm',
      Cell: DistanceCell,
    };

    const employmentTypeMismatch: Column<ApplicantColumnDefinition> = {
      Header: '',
      accessor: 'employmentTypeMismatch',
      Cell: EmploymentTypeMismatchCell,
    };

    const actions: Column<ArchiveTableColumnDefinition> = {
      accessor: 'actions',
      Header: '',
      Cell: ActionCell,
    };

    return [
      flexWorkerImage,
      flexWorkerFullName,
      hasRequiredCertificates,
      rating,
      attendanceRate,
      workedShifts,
      hourlyRate,
      ...(showAppliedShifts ? [appliedShifts] : []),
      phone,
      experience,
      contractHours,
      ...(hideEmploymentTypeFields ? [] : [employmentTypeMismatch]),
      distance,
      actions,
    ];
  }, [
    flexWorkerImage,
    t,
    showAppliedShifts,
    employmentType,
    projectTable,
    hideEmploymentTypeFields,
  ]);
};

const FlexWorkerCell = ({ value }: ApplicantCellProps<'flexWorkerFullName'>) => {
  return (
    <TableCell style={{ maxWidth: '90px' }}>
      <Tooltip title={value}>
        <Typography noWrap>{value}</Typography>
      </Tooltip>
    </TableCell>
  );
};

const RatingCell = ({ value }: ApplicantCellProps<'rating'>) => {
  return value ? <Typography noWrap>{value}%</Typography> : null;
};

const EmploymentTypeMismatchCell = ({ value }: ApplicantCellProps<'employmentTypeMismatch'>) => {
  const { t } = useTranslation('hq-screen-specific');

  if (value) {
    return (
      <Tooltip
        placement="top"
        title={t('work.applicants.employment_type_not_supported', {
          ns: 'hq-screen-specific',
        })}
      >
        <Box display="flex" alignItems="center" data-testid="employment-type-mismatch-warning">
          <StyledIcon as={IconWarning} />
        </Box>
      </Tooltip>
    );
  }
  return <></>;
};

const CertificateChecksCell = ({ value }: ApplicantCellProps<'hasRequiredCertificates'>) => {
  return value ? (
    <Tooltip
      title="Deze flexwerker is in het bezit van alle benodigde certificaten"
      aria-label="certificate-check"
    >
      <Box paddingLeft={5}>
        <IconCertificate width={24} />
      </Box>
    </Tooltip>
  ) : null;
};

const AttendanceRateCell = ({ value }: ApplicantCellProps<'attendanceRate'>) => {
  return value ? <TableCell>{value}%</TableCell> : null;
};

const WorkedShiftsCell = ({ value }: ApplicantCellProps<'workedShifts'>) => {
  return <TableCell>{pluralizeWithValue(value, 'shift', 'shifts', '0')}</TableCell>;
};

const AppliedShiftsCell = ({ value }: ApplicantCellProps<'appliedShifts'>) => {
  return <TableCell>{value}</TableCell>;
};

const PhoneCell = ({ value }: ApplicantCellProps<'appliedShifts'>) => {
  return <TableCell>{value}</TableCell>;
};

const ExperienceCell = ({ value }: ApplicantCellProps<'appliedShifts'>) => {
  return (
    <TableCell>
      <Box aria-label="experience-cell">{convertToExperienceFormat(value)}</Box>
    </TableCell>
  );
};

const DistanceCell = ({ value }: ApplicantCellProps<'appliedShifts'>) => {
  if (value === undefined) {
    return <TableCell>-</TableCell>;
  }
  const roundedValue = Math.round((value + Number.EPSILON) * 10) / 10;
  return <TableCell>{roundedValue} km</TableCell>;
};

const ContractHoursCell = ({ value }: ApplicantCellProps<'contractHoursData'>) => {
  return <ContractHoursChip value={value} />;
};

const ActionCell = ({ value, getRowActions }: ApplicantCellProps<'actions'>) => {
  const actions = getRowActions(value);

  if (actions.length === 0) {
    return null;
  }

  return (
    <ContextMenuButton menuId={`claimant-actions-menu-${value.workerId}`}>
      {actions}
    </ContextMenuButton>
  );
};

const StyledIcon = styled('svg')(({ theme }) => ({
  '& path': {
    stroke: theme.palette.lwDanger[100],
    strokeWidth: theme.spacing(0.5),
  },
}));
